import { FormItemProps } from 'antd'
import { RuleObject } from 'antd/es/form'
import dayjs, { Dayjs } from 'dayjs'
import { useTranslation } from 'react-i18next'

type ReturnValidations = Record<
  string,
  | Required<FormItemProps>['rules'][number]
  | ((...args: never[]) => Required<FormItemProps>['rules'][number])
>

export const useValidationsFields = () => {
  const { t } = useTranslation()

  return {
    fullName: {
      transform: (value) => value?.trim(),
      pattern: /^[A-Za-z\-'.\s]{2,50}$/,
      message: t('invalid_name_format')
    },
    customPersonalCode: {
      transform: (value) => value?.trim(),
      pattern: /^\d{12,15}$/,
      message: t('invalid_medical_personal_code_format')
    },
    passportSeriesNumber: {
      transform: (value) => value?.trim(),
      pattern: /^.{7,10}$/,
      message: t('invalid_passport_length')
    },
    required: {
      required: true,
      message: t('this_field_is_required')
    },
    companyName: {
      transform: (value) => value?.trim(),
      pattern: /^[\p{Script=Latin}\p{N}\s.,\-'"()&/;„”]{2,100}$/u,
      message: t('invalid_company_name_format')
    },
    phoneNumber: {
      transform: (value) => {
        return value?.replace('+', '')?.trim()
      },
      pattern: /^\d{11,13}$/,
      message: t('invalid_phone_number_format')
    },
    personalCode: {
      transform: (value) => value?.trim(),
      pattern: /^\d{13}$/,
      message: t('invalid_personal_code_format')
    },
    vehicleRegistrationNumber: {
      transform: (value) => value?.trim(),
      pattern: /^.{4,}$/,
      message: t('invalid_vehicle_registration_number')
    },
    beforeYear: (minDate?: Dayjs) => ({
      validator: (_: RuleObject, value: Dayjs) => {
        if (!value || !minDate) return Promise.resolve()

        if (value.isBefore(minDate, 'year')) {
          return Promise.reject(
            new Error(t('year_min_value', { year: minDate?.year() }))
          )
        }

        return Promise.resolve()
      }
    }),
    olderThan: (age?: number) => ({
      validator: (_: RuleObject, value: Dayjs) => {
        if (!value || !age) return Promise.resolve()

        const today = dayjs()
        const limitDate = today.subtract(age, 'year')

        if (value.isAfter(limitDate, 'day')) {
          return Promise.reject(new Error(t('must_be_older_than', { age })))
        }

        return Promise.resolve()
      }
    })
  } satisfies ReturnValidations
}
